// hooks/use-collaborate-broadcast.js
// ═══════════════════════════════════════════════════════════════════
// Cross-client live streaming for Collaborate's askShift.
//
// The asker streams via SSE (handled in use-collaborate-chat.js).
// The OTHER participant subscribes here to a Supabase realtime
// broadcast channel (`space-stream:<workspaceId>`) and receives:
//
//   shift_start  → render a streaming placeholder bubble
//   shift_token  → append text + dispatch canvas streaming events
//                  if a fenced HTML block is detected
//   shift_end    → finalize the bubble (the postgres_changes INSERT
//                  that follows will dedup via use-space-v2's
//                  content-match logic)
//
// Skip events broadcast by your own user id — those came from your
// own SSE stream and you've already rendered them locally.
// ═══════════════════════════════════════════════════════════════════

import { useEffect, useRef } from 'react';
import { FENCE_OPEN_RE } from '../components/collaborate-message';

export function useCollaborateBroadcast({
  workspaceId,
  supabase,
  userId,
  setMessages,
  enabled = true,
}) {
  // Map stream_id → local placeholder id, plus per-stream raw text
  // and fence state for canvas dispatch. Refs because React state
  // would re-render on every token tick — wasteful.
  const streamsRef = useRef({});

  useEffect(() => {
    if (!workspaceId || !supabase || !enabled) return;

    const channel = supabase
      .channel(`space-stream:${workspaceId}`)
      .on('broadcast', { event: 'shift_start' }, (payload) => {
        const { stream_id, asker } = payload?.payload || {};
        if (!stream_id) return;
        if (asker && asker === userId) return; // own stream — already rendered locally

        const placeholderId = `bcast-${stream_id}`;
        streamsRef.current[stream_id] = {
          placeholderId,
          rawText: '',
          fenceOpen: false,
          fenceComplete: false,
          fenceType: null,
          lastCanvasDispatch: 0,
        };
        setMessages(prev => {
          if (prev.some(m => m.id === placeholderId)) return prev;
          return [...prev, {
            id: placeholderId,
            role: 'assistant',
            content: '',
            sender_id: 'shift',
            streaming: true,
            created_at: new Date().toISOString(),
          }];
        });
      })
      .on('broadcast', { event: 'shift_token' }, (payload) => {
        const { stream_id, asker, t } = payload?.payload || {};
        if (!stream_id || !t) return;
        if (asker && asker === userId) return;

        const state = streamsRef.current[stream_id];
        if (!state) return; // shift_start was missed — ignore
        state.rawText += t;
        const raw = state.rawText;

        // Update the placeholder content
        setMessages(prev => prev.map(m =>
          m.id === state.placeholderId
            ? { ...m, content: m.content + t }
            : m
        ));

        // Fence detection → dispatch the same canvas events the
        // asker fires, so the invitee's canvas builds live too.
        if (!state.fenceOpen && FENCE_OPEN_RE.test(raw)) {
          state.fenceOpen = true;
          state.fenceType = raw.match(FENCE_OPEN_RE)?.[1] || 'html';
          window.dispatchEvent(new CustomEvent('workstation:canvas-building', {
            detail: { type: state.fenceType },
          }));
        }
        if (state.fenceOpen && !state.fenceComplete) {
          const openMatch = raw.match(FENCE_OPEN_RE);
          if (openMatch) {
            const afterOpen = raw.slice(openMatch.index + openMatch[0].length);
            const closeIdx = afterOpen.indexOf('\n```');
            if (closeIdx >= 0) {
              state.fenceComplete = true;
              const fencedContent = afterOpen.slice(0, closeIdx).trim();
              window.dispatchEvent(new CustomEvent('workstation:canvas-streaming', {
                detail: { content: fencedContent, type: state.fenceType, done: true },
              }));
            } else {
              // Throttle partial dispatches to avoid hammering the iframe
              const now = Date.now();
              if (now - state.lastCanvasDispatch > 500) {
                state.lastCanvasDispatch = now;
                window.dispatchEvent(new CustomEvent('workstation:canvas-streaming', {
                  detail: { content: afterOpen, type: state.fenceType, done: false },
                }));
              }
            }
          }
        }
      })
      .on('broadcast', { event: 'shift_end' }, (payload) => {
        const { stream_id, asker, msg_id, text, error } = payload?.payload || {};
        if (!stream_id) return;
        if (asker && asker === userId) return;

        const state = streamsRef.current[stream_id];
        if (!state) return;

        // Clear the building pulse
        window.dispatchEvent(new CustomEvent('workstation:canvas-building', {
          detail: { done: true },
        }));

        if (error) {
          // Stream errored — drop the placeholder
          setMessages(prev => prev.filter(m => m.id !== state.placeholderId));
        } else {
          // Finalize: rename the placeholder to the DB id (if known)
          // and replace content with the canonical full text. The
          // postgres_changes INSERT that fires shortly after will
          // dedup via use-space-v2's content-match logic — no twin.
          setMessages(prev => prev.map(m =>
            m.id === state.placeholderId
              ? { ...m, id: msg_id || state.placeholderId, content: text || m.content, streaming: false }
              : m
          ));
        }

        delete streamsRef.current[stream_id];
      })
      .subscribe();

    return () => {
      // Clear any in-flight placeholders on unmount
      const placeholders = Object.values(streamsRef.current).map(s => s.placeholderId);
      if (placeholders.length > 0) {
        setMessages(prev => prev.filter(m => !placeholders.includes(m.id)));
      }
      streamsRef.current = {};
      try { supabase.removeChannel(channel); } catch { /* drop */ }
    };
    // eslint-disable-next-line
  }, [workspaceId, supabase, userId, enabled]);
}
